/*
 * Players — full event roster with profile info, grouped by invite
 * status (Account linked, Invite pending, Not invited, Declined).
 *
 * Each section is a labeled grid of player cards. Empty sections are
 * hidden so the page reads cleanly when, say, every player has joined.
 * Within a section players sort alphabetically.
 *
 * Captains and owners (canEdit) see contact info and can click a card
 * to open PlayerEditModal. Everyone else gets a read-only view.
 */

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus, Mail, Phone } from 'lucide-react';

import { api } from '@/lib/api';
import { useTournamentStore } from '@/store/tournamentStore';
import { AppShell } from '@/components/AppShell';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { TeamAvatar } from '@/components/TeamAvatar';
import { PlayerEditModal } from './PlayerEditModal';
import {
  getInviteStatus,
  inviteStatusLabel,
  inviteStatusColor,
  type InviteStatus,
} from '@/lib/inviteStatus';
import type { EventPlayer, TournamentTeam } from '@golf-app/shared';

/** Small inline status pill — colored dot + label. Used to surface
 *  "Account linked / Invite pending / Not invited" at a glance on each roster
 *  card. Kept compact so it fits in the existing sub-line. */
function InviteStatusPill({ player }: { player: EventPlayer }) {
  const s = getInviteStatus(player);
  const color = inviteStatusColor(s);
  return (
    <span className="t-player-card-invite-status" style={{ color }}>
      <span className="t-player-card-status-dot" style={{ background: color }} />
      {inviteStatusLabel(s)}
    </span>
  );
}

function fmtHcp(h: number | null | undefined): string | null {
  if (h == null) return null;
  const abs = Math.abs(h);
  // One-decimal precision: 12 → "12", 12.5 → "12.5". Avoids "12.0".
  const rounded = Math.round(abs * 10) / 10;
  const str = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return h < 0 ? `+${str}` : str;
}

// Section order on the page. Linked accounts first (you're done, here are
// the confirmed players), then Invite pending (waiting on the recipient),
// then Not invited (the actionable bucket the captain needs to attack
// first), then Declined at the bottom for the rare "no thanks" case.
const SECTION_ORDER: InviteStatus[] = ['joined', 'pending', 'not_invited', 'declined'];
const SECTION_HEADERS: Record<InviteStatus, { title: string; hint: string }> = {
  joined:      { title: 'Account linked',  hint: 'These players have linked an account and are ready to go.' },
  pending:     { title: 'Invited',         hint: 'Invite sent. Waiting for them to claim their spot.' },
  not_invited: { title: 'Not invited yet', hint: 'Add an email or phone in the edit modal to send an invite.' },
  declined:    { title: 'Declined',        hint: 'They opted out. Remove or follow up directly.' },
};

export default function Players() {
  const { eventId } = useParams<{ eventId: string }>();
  const { activeEvent, setActiveEvent } = useTournamentStore();
  const [loading, setLoading] = useState(!activeEvent || activeEvent.id !== eventId);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    try {
      const ev = await api.getTournament(eventId);
      setActiveEvent(ev);
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load event");
    } finally {
      setLoading(false);
    }
  }, [eventId, setActiveEvent]);

  useEffect(() => { load(); }, [load]);

  const event = activeEvent?.id === eventId ? activeEvent : null;
  // Captains share owner-level edit rights per the roles spec — they
  // can open the edit modal on any player and see contact info. Players
  // still see a static read-only roster with no PII.
  const canEdit = event?.myRole === 'owner' || event?.myRole === 'captain';
  const isOwner = event?.myRole === 'owner';
  // Holds the player whose card was clicked. PlayerEditModal opens for
  // owner or captain; players see the card as a read-only div.
  const [editing, setEditing] = useState<EventPlayer | null>(null);

  const teamMap = useMemo(
    () => new Map((event?.teams ?? []).map((t) => [t.id, t])),
    [event],
  );

  const players = useMemo(() => {
    // Show every active event_player including the event owner. Earlier
    // versions filtered roleType==='owner' out of the roster, which
    // hid the captain from their own player list and was confusing
    // ("I'm in this event, why don't I see myself?"). The owner row
    // still gets a CAPTAIN-style identity treatment elsewhere; here
    // it just lists alongside everyone else.
    const active = (event?.players ?? []).filter((p) => p.status !== 'removed');
    // Alphabetical by display name — easiest to scan in a roster table.
    return active.slice().sort((a, b) =>
      (a.displayName ?? '').localeCompare(b.displayName ?? '', undefined, { sensitivity: 'base' }),
    );
  }, [event]);

  // Bucket players by their derived invite status. Each bucket keeps
  // the alphabetical ordering from the parent sort.
  const grouped = useMemo(() => {
    const groups: Record<InviteStatus, EventPlayer[]> = {
      joined: [], pending: [], not_invited: [], declined: [],
    };
    for (const p of players) {
      groups[getInviteStatus(p)].push(p);
    }
    return groups;
  }, [players]);

  if (loading) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId ?? ''}`, label: 'Event' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }
  if (!event) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId ?? ''}`, label: 'Event' }}>
        <div className="t-empty"><div className="t-empty-title">Event not found</div></div>
      </AppShell>
    );
  }

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
      <section className="t-hero">
        <div className="t-hero-row">
          <div>
            <h1>Players</h1>
            <p>Roster info for everyone in this event. Edit teams on the Teams tab.</p>
          </div>
          {/* canEdit covers owner + captain, matching the backend's
              add-player permission. Players don't see this button. */}
          {canEdit && (
            <Link to={`/app/tournaments/${eventId}/players/add`} className="t-btn t-btn-primary">
              <Plus /> Add Player
            </Link>
          )}
        </div>
      </section>


      {error && <div className="t-error">{error}</div>}

      {players.length === 0 ? (
        <div className="t-empty">
          <div className="t-empty-title">No players yet</div>
          <div className="t-empty-hint">
            {canEdit ? 'Add players to start building teams.' : 'The organizer hasn’t added players yet.'}
          </div>
        </div>
      ) : (
        SECTION_ORDER.map((key) => {
          const items = grouped[key];
          if (items.length === 0) return null;
          const { title, hint } = SECTION_HEADERS[key];
          const accent = inviteStatusColor(key);
          return (
            <section key={key} style={{ marginBottom: 22 }}>
              <header
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 10,
                  marginBottom: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                  background: accent, flexShrink: 0, transform: 'translateY(-1px)',
                }} aria-hidden />
                <h2 style={{
                  margin: 0,
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: accent,
                }}>
                  {title}
                </h2>
                <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-mut)' }}>
                  {items.length}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-mut)', flex: '1 1 auto' }}>
                  {hint}
                </span>
              </header>

              <div
                style={{
                  display: 'grid',
                  // minmax(min(100%, 260px), 1fr) is the canonical safe form
                  // for responsive auto-fill grids — falls back to single
                  // column on narrow phones (327px content at 375px viewport)
                  // instead of overflowing horizontally.
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
                  gap: 10,
                  alignItems: 'start',
                }}
              >
                {items.map((p) => {
                  return (
                    <PlayerCard
                      key={p.id}
                      player={p}
                      team={p.teamId ? teamMap.get(p.teamId) ?? null : null}
                      canEdit={canEdit}
                      onClick={canEdit ? () => setEditing(p) : undefined}
                    />
                  );
                })}
              </div>
            </section>
          );
        })
      )}

      {editing && (
        <PlayerEditModal
          player={editing}
          teams={event.teams}
          isOwner={isOwner}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </AppShell>
  );
}

// ── PlayerCard ─────────────────────────────────────────────────────────────
//
// Pulled out of the page body so the section loop can render it cleanly.
// Behavior is unchanged from the pre-grouping render: button when the
// caller can edit, plain div when read-only; email/phone only visible
// to owner+captain.

function PlayerCard({
  player: p, team, canEdit, onClick,
}: {
  player: EventPlayer;
  team: TournamentTeam | null;
  canEdit: boolean;
  onClick?: () => void;
}) {
  const hcp = fmtHcp(p.eventHandicapIndex);
  const Tag: any = canEdit ? 'button' : 'div';
  return (
    <Tag
      type={canEdit ? 'button' : undefined}
      onClick={onClick}
      className={`t-player-card${canEdit ? ' t-player-card-editable' : ''}`}
    >
      <div className="t-player-card-primary">
        <PlayerAvatar avatarUrl={p.avatarUrl} displayName={p.displayName} size={40} />
        <div className="t-player-card-identity">
          {/* The player's full name is the card hero. It owns the complete
              first row and wraps naturally instead of competing with team
              or captain badges for horizontal space. */}
          <span className="t-player-card-name">{p.displayName}</span>

          {(p.roleType === 'captain' || team) && (
            <div className="t-player-card-badges">
              {p.roleType === 'captain' && (
                <span className="t-player-card-captain">CAPTAIN</span>
              )}
              {team && (
                <span
                  className="t-player-card-team"
                  style={{
                    '--player-team-color': team.primaryColor ?? 'var(--text-sec)',
                  } as CSSProperties}
                >
                  <TeamAvatar team={team} size={12} />
                  <span>{team.name}</span>
                </span>
              )}
            </div>
          )}

          <div className="t-player-card-meta">
            <InviteStatusPill player={p} />
            {hcp && (
              <>
                <span aria-hidden>·</span>
                <span className="t-player-card-hcp">HCP {hcp}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Email and phone are owner/captain-only. Other roles don't see
          contact info; that's PII entered for invite purposes, not a
          public roster directory. Backend still returns the values so
          owners can manage them — the frontend gates render. */}
      {canEdit && (p.email || p.phone) && (
        <div className="t-player-card-contact">
          {p.email && (
            <div>
              <Mail aria-hidden />
              <span className="t-player-card-email">{p.email}</span>
            </div>
          )}
          {p.phone && (
            <div>
              <Phone aria-hidden />
              <span>{p.phone}</span>
            </div>
          )}
        </div>
      )}
    </Tag>
  );
}
